/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import RefreshIcon from '@mui/icons-material/Refresh';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { IconButton, Link as MuiLink, Stack, Typography } from '@mui/material';
import { type AbsoluteURL, formatDateLocal, type RecentConfigRecord } from '@poweralpha/common';
import RecordList from '../../react-components/RecordList';
import { InfoPill } from '../../react-components/text';
import Widget from '../../react-components/Widget';
import { serviceColorMap } from '../../styles';
import { useRecentConfigsQuery } from '../api';

const getConfigLink = (config: RecentConfigRecord): AbsoluteURL => {
  const queryParams = `config=${config.id}&version=${config.version}`;
  switch (config.service) {
    case 'PVSim':
      return `/pv/pvsim/simulate?${queryParams}`;
    case 'PVCosting':
      return `/pv/costing/simulate?${queryParams}`;
    case 'ESS':
      return `/ess/costing/simulate?${queryParams}`;
    case 'PlantSim':
      return `/plant/simulate?${queryParams}`;
    default:
      return '/projects';
  }
};

const getRow = (config: RecentConfigRecord) => (
  <Stack
    spacing={0.5}
    sx={{
      width: '100%',
    }}
  >
    <Stack
      direction="row"
      spacing={1}
      sx={{
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <MuiLink
        component="span"
        underline="always"
        sx={{
          fontSize: 13,
          fontWeight: 600,
          lineHeight: 1.2,
          color: 'primary.main',
        }}
        noWrap
      >
        {config.name || 'Untitled config'}
      </MuiLink>
      <InfoPill label={config.service} color={serviceColorMap[config.service]} height={18} />
    </Stack>
    <Stack
      direction="row"
      spacing={1}
      sx={{
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <Stack
        direction="row"
        spacing={0.75}
        sx={{
          alignItems: 'center',
          minWidth: 0,
        }}
      >
        <ScheduleIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
        <Typography
          variant="caption"
          noWrap
          sx={{
            color: 'text.secondary',
          }}
        >
          {formatDateLocal(config.created_at)}
        </Typography>
      </Stack>
      <Typography
        variant="caption"
        noWrap
        sx={{
          color: 'text.secondary',
          textAlign: 'right',
        }}
      >
        v{config.version}
      </Typography>
    </Stack>
  </Stack>
);

function RecentConfigs() {
  const recentConfigsQuery = useRecentConfigsQuery({
    limit: 10,
  });

  return (
    <Widget
      title="My recent configs"
      actions={
        <IconButton
          loading={recentConfigsQuery.isRefetching}
          size="small"
          onClick={() => recentConfigsQuery.refetch()}
        >
          <RefreshIcon fontSize="small" />
        </IconButton>
      }
    >
      <RecordList
        label="config"
        queryResult={recentConfigsQuery}
        getRow={getRow}
        getLink={getConfigLink}
        idKey="id"
      />
    </Widget>
  );
}

export default RecentConfigs;
